import { ethers } from 'ethers';
import { RequestService } from './requestService';
import { withRateLimitRetry } from '../utils/rateLimitRetry';

export class ContractListener {
  constructor(contract, contractAddress) {
    this.contract = contract;
    this.contractAddress = contractAddress;
    this.listeners = new Map();
  }

  /**
   * Start listening to ConfidentialMirror events
   */
  startListening() {
    if (!this.contract || this.contract.isStellarSoroban || typeof this.contract.on !== 'function') {
      console.log('ContractListener: skipping ethers events (Soroban facade or no contract.on)');
      return;
    }
    console.log('Starting contract event listener...');
    
    // Update request statuses on processed events
    const markCompleted = async (label, event, extra = {}) => {
      try {
        // Get chainId from network (with rate limit retry)
        const network = await withRateLimitRetry(
          () => this.contract.provider.getNetwork(),
          { retries: 5, baseDelayMs: 1000 }
        );
        const chainId = network.chainId;
        
        // Extract userAddress from event parameters (owner, sender, or recipient depending on event type)
        const userAddress = extra.owner || extra.sender || null;
        
        // Use txId as requestId when available; fallback to transactionHash only if necessary
        const requestId = extra.txId || extra.requestId || event.transactionHash;
        await RequestService.updateRequestStatus(requestId, 'completed', {
          finalizedAt: new Date(),
          finalizedBlockNumber: event.blockNumber,
          finalizedTransactionHash: event.transactionHash,
          event: label,
          ...extra
        }, chainId, userAddress);
        console.log('Request status updated (', label, '):', requestId, 'on chain', chainId, 'for user', userAddress);
      } catch (error) {
        console.error('Error updating request status:', error);
      }
    };

    this.contract.on('CreateAccountProcessed', async (owner, txId, ok, err, elgamalPubkey, event) => {
      await markCompleted('CreateAccountProcessed', event, { owner, txId: txId.toString(), ok, err, requestId: txId.toString() });
    });

    this.contract.on('DepositProcessed', async (owner, txId, ok, err, token, c1, c2, pcc, mpcc, availShare, pendShare, event) => {
      await markCompleted('DepositProcessed', event, { owner, txId: txId.toString(), ok, err, token, requestId: txId.toString() });
    });

    this.contract.on('TransferProcessed', async (sender, recipient, txId, ok, err, token, sC1, sC2, rC1, rC2, spcc, sAvailShare, rpcc, rmpcc, rPendShare, event) => {
      // Update sender's record
      await markCompleted('TransferProcessed', event, { sender, recipient, txId: txId.toString(), ok, err, token, requestId: txId.toString() });
      
      // Update receiver's record (should already exist from when sender created it)
      try {
        const network = await withRateLimitRetry(
          () => this.contract.provider.getNetwork(),
          { retries: 5, baseDelayMs: 1000 }
        );
        const chainId = network.chainId;
        const receivedRequestId = `received-${txId.toString()}-${recipient.toLowerCase()}`;
        
        // Try to update existing receiver record
        try {
          await RequestService.updateRequestStatus(receivedRequestId, ok ? 'completed' : 'failed', {
            finalizedAt: new Date(),
            finalizedBlockNumber: event.blockNumber,
            finalizedTransactionHash: event.transactionHash,
            event: 'TransferProcessed'
          }, chainId, recipient.toLowerCase());
          console.log('Updated receiver record:', receivedRequestId, 'on chain', chainId, 'for user', recipient);
        } catch (updateError) {
          // If receiver record doesn't exist (old transfer), create it
          console.log('Receiver record not found, creating it:', receivedRequestId);
          
          const block = await withRateLimitRetry(
            () => event.getBlock(),
            { retries: 5, baseDelayMs: 1000 }
          );
          
          await RequestService.addRequest({
            requestId: receivedRequestId,
            userAddress: recipient.toLowerCase(),
            operationKind: 2,
            type: 'received',
            details: {
              sender: sender,
              recipient: recipient,
              token: token
            },
            transactionHash: event.transactionHash,
            chainId: chainId,
            status: ok ? 'completed' : 'failed',
            blockNumber: event.blockNumber
          });
          console.log('Created receiver record:', receivedRequestId, 'on chain', chainId);
        }
      } catch (error) {
        console.error('Error handling receiver record:', error);
      }
    });

    this.contract.on('WithdrawProcessed', async (owner, txId, ok, err, token, amount, c1, c2, pcc, mpcc, availShare, event) => {
      await markCompleted('WithdrawProcessed', event, { owner, txId: txId.toString(), ok, err, token, amount: amount.toString(), requestId: txId.toString() });
    });

    console.log('Contract event listener started');
  }

  /**
   * Stop listening to events
   */
  stopListening() {
    console.log('Stopping contract event listener...');
    if (!this.contract || this.contract.isStellarSoroban || typeof this.contract.removeAllListeners !== 'function') {
      return;
    }
    this.contract.removeAllListeners('CreateAccountProcessed');
    this.contract.removeAllListeners('DepositProcessed');
    this.contract.removeAllListeners('TransferProcessed');
    this.contract.removeAllListeners('WithdrawProcessed');
    console.log('Contract event listener stopped');
  }

  /**
   * Check if a request still exists in the contract (for manual verification)
   */
  async isRequestPending(requestId) {
    try {
      // Try to get the request from the contract (with rate limit retry)
      // If it doesn't exist (deleted), it means it's completed
      const request = await withRateLimitRetry(
        () => this.contract.requests(requestId),
        { retries: 5, baseDelayMs: 1000 }
      );
      return request.user !== ethers.constants.AddressZero;
    } catch (error) {
      // If there's an error, assume the request doesn't exist (completed)
      return false;
    }
  }

  /**
   * Convert operation kind number to string
   */
  getOperationKindName(kind) {
    const operationKinds = [
      'CreateConfidentialAccount',
      'Deposit', 
      'TransferConfidential',
      'ApplyPending',
      'Withdraw'
    ];
    return operationKinds[kind] || 'Unknown';
  }

  /**
   * Manual check for all pending requests of a user
   */
  async checkUserPendingRequests(userAddress) {
    try {
      // Get all pending requests from Firebase
      const requests = await RequestService.getUserRequests(userAddress);
      const pendingRequests = requests.filter(req => req.status === 'pending');
      
      console.log(`Checking ${pendingRequests.length} pending requests for user ${userAddress}`);
      
      // Get chainId for updates (with rate limit retry)
      const network = await withRateLimitRetry(
        () => this.contract.provider.getNetwork(),
        { retries: 5, baseDelayMs: 1000 }
      );
      const chainId = network.chainId;
      
      for (const request of pendingRequests) {
        // Extract the actual requestId (remove chainId:userAddress: or chainId: prefix if present)
        // Format can be: "chainId:userAddress:requestId", "chainId:requestId", or just "requestId"
        let actualRequestId = request.requestId;
        if (request.requestId.includes(':')) {
          const parts = request.requestId.split(':');
          // If 3 parts, it's chainId:userAddress:requestId, take the last part
          // If 2 parts, it's chainId:requestId, take the last part
          actualRequestId = parts[parts.length - 1];
        }
        
        const isStillPending = await this.isRequestPending(actualRequestId);
        
        if (!isStillPending) {
          console.log(`Request ${request.requestId} is no longer pending, updating status`);
          await RequestService.updateRequestStatus(actualRequestId, 'completed', {
            success: true, // Assume success if request was processed
            finalizedAt: new Date()
          }, request.chainId || chainId, request.userAddress || userAddress);
        }
      }
    } catch (error) {
      console.error('Error checking pending requests:', error);
    }
  }

  /**
   * Backfill received transaction records for a user by scanning past TransferProcessed events
   */
  async backfillReceivedTransactions(userAddress) {
    try {
      console.log('Backfilling received transactions for:', userAddress);
      
      // Get current block (with rate limit retry)
      const currentBlock = await withRateLimitRetry(
        () => this.contract.provider.getBlockNumber(),
        { retries: 5, baseDelayMs: 1000 }
      );
      const fromBlock = Math.max(0, currentBlock - 10000); // Last 10k blocks
      
      console.log(`Scanning blocks ${fromBlock} to ${currentBlock} for received transfers...`);
      
      // Query TransferProcessed events where user is recipient (with rate limit retry)
      const filter = this.contract.filters.TransferProcessed(null, userAddress);
      const events = await withRateLimitRetry(
        () => this.contract.queryFilter(filter, fromBlock, currentBlock),
        { retries: 5, baseDelayMs: 1000 }
      );
      
      console.log(`Found ${events.length} received transfer events`);
      
      // Get chainId (with rate limit retry)
      const network = await withRateLimitRetry(
        () => this.contract.provider.getNetwork(),
        { retries: 5, baseDelayMs: 1000 }
      );
      const chainId = network.chainId;
      
      // Create received records for each event
      let created = 0;
      for (const event of events) {
        try {
          const [sender, recipient, txId, ok] = event.args;
          const receivedRequestId = `received-${txId.toString()}-${recipient.toLowerCase()}`;
          
          // Check if record already exists (check all formats: chainId:userAddress:requestId, chainId:requestId, or just requestId)
          const existingRequests = await RequestService.getUserRequests(userAddress);
          // Check all formats by extracting the actual requestId from each
          const exists = existingRequests.some(req => {
            const reqId = req.requestId || req.id;
            // Extract actual requestId (last part after splitting by ':')
            let actualReqId = reqId;
            if (reqId.includes(':')) {
              const parts = reqId.split(':');
              actualReqId = parts[parts.length - 1];
            }
            return actualReqId === receivedRequestId;
          });
          
          if (!exists) {
            const block = await withRateLimitRetry(
              () => event.getBlock(),
              { retries: 5, baseDelayMs: 1000 }
            );
            
            await RequestService.addRequest({
              requestId: receivedRequestId,
              userAddress: recipient.toLowerCase(),
              operationKind: 2, // Transfer
              type: 'received',
              details: {
                sender: sender,
                recipient: recipient,
                token: event.args.token
              },
              transactionHash: event.transactionHash,
              chainId: chainId,
              status: ok ? 'completed' : 'failed',
              blockNumber: event.blockNumber
            });
            
            created++;
            console.log(`Created received record for tx ${txId.toString()}`);
          }
        } catch (error) {
          console.error('Error creating received record:', error);
        }
      }
      
      console.log(`Backfill complete: created ${created} new received transaction records`);
      return created;
    } catch (error) {
      console.error('Error backfilling received transactions:', error);
      throw error;
    }
  }
}
